/**
 * Finances de l'école (données du serveur) : catégories de recettes et de dépenses (par cycle) et
 * opérations. Une dépense est rattachée à une charge : sa catégorie est la déclinaison de la charge dans
 * le cycle. Les recettes des reçus et les dépenses de la paie sont automatiques (lecture seule).
 */
import { http, orUndefined } from "./http";
import { getCharges } from "./chargeService";
import { getConsultedYearRange } from "./schoolService";
import type { Charge, ChargeCategory, FinanceCategory, Transaction, TransactionType, PaymentMethod } from "@/types";

type ApiType = "RECETTE" | "DEPENSE";

interface ApiCategory {
  id: number;
  name: string;
  type: ApiType;
  cycleId: number;
  icon: string | null;
  chargeId: number | null;
  monthlyBudget: number;
}

interface ApiTransaction {
  id: number;
  type: ApiType;
  categoryId: number;
  category?: ApiCategory;
  amount: number;
  date: string;
  cycleId: number;
  description: string | null;
  paymentId: number | null;
  payrollRunId: number | null;
  method: PaymentMethod | null;
  recordedByName: string | null;
}

const toType = (t: ApiType): TransactionType => (t === "RECETTE" ? "recette" : "depense");
const fromType = (t: TransactionType): ApiType => (t === "recette" ? "RECETTE" : "DEPENSE");

function toCategory(c: ApiCategory): FinanceCategory {
  return {
    id: String(c.id),
    name: c.name,
    type: toType(c.type),
    cycleId: String(c.cycleId),
    icon: c.icon ?? "ri-price-tag-3-line",
    chargeId: c.chargeId === null ? undefined : String(c.chargeId),
    monthlyBudget: c.monthlyBudget,
  };
}

function toTransaction(t: ApiTransaction): Transaction {
  return {
    id: String(t.id),
    type: toType(t.type),
    categoryId: String(t.categoryId),
    chargeId: t.category?.chargeId ? String(t.category.chargeId) : undefined,
    amount: t.amount,
    date: t.date,
    cycleId: String(t.cycleId),
    description: t.description ?? "",
    source: t.paymentId !== null ? "receipt" : t.payrollRunId !== null ? "payroll" : null,
    method: t.method ?? undefined,
    recordedByName: t.recordedByName ?? undefined,
  };
}

/* -------------------------------------------------------------------------- */
/* Catégories                                                                  */
/* -------------------------------------------------------------------------- */

export async function getCategories(): Promise<FinanceCategory[]> {
  return (await http.getAll<ApiCategory>("/finance/categories")).map(toCategory);
}

export async function addCategory(payload: Omit<FinanceCategory, "id">): Promise<FinanceCategory> {
  return toCategory(
    await http.post<ApiCategory>("/finance/categories", {
      name: payload.name.trim(),
      type: fromType(payload.type),
      cycleId: Number(payload.cycleId),
      icon: orUndefined(payload.icon),
    }),
  );
}

export async function updateCategory(id: string, patch: Partial<FinanceCategory>): Promise<FinanceCategory> {
  return toCategory(
    await http.put<ApiCategory>(`/finance/categories/${id}`, {
      name: patch.name?.trim(),
      icon: patch.icon === undefined ? undefined : orUndefined(patch.icon),
    }),
  );
}

export async function deleteCategory(id: string): Promise<void> {
  await http.delete(`/finance/categories/${id}`);
}

/* -------------------------------------------------------------------------- */
/* Opérations                                                                  */
/* -------------------------------------------------------------------------- */

/** Opérations de l'année scolaire consultée (bornes de ses dates). */
export async function getTransactions(): Promise<Transaction[]> {
  const range = getConsultedYearRange();
  return (
    await http.getAll<ApiTransaction>("/transactions", range ? { dateFrom: range.from, dateTo: range.to } : {})
  ).map(toTransaction);
}

/** Catégorie de l'opération : pour une dépense, la déclinaison de sa charge dans le cycle. */
async function categoryFor(payload: Pick<Transaction, "type" | "categoryId" | "chargeId" | "cycleId">): Promise<number> {
  if (payload.type === "depense" && payload.chargeId) {
    const charge = (await getCharges()).find((c) => c.id === payload.chargeId);
    const categoryId = charge?.cycleCategoryIds[payload.cycleId];
    if (!categoryId) throw new Error("Cette charge ne couvre pas ce cycle : ajoutez-le dans le budget de la charge.");
    return Number(categoryId);
  }
  return Number(payload.categoryId);
}

export async function addTransaction(payload: Omit<Transaction, "id">): Promise<Transaction> {
  return toTransaction(
    await http.post<ApiTransaction>("/transactions", {
      type: fromType(payload.type),
      categoryId: await categoryFor(payload),
      amount: payload.amount,
      date: payload.date,
      cycleId: Number(payload.cycleId),
      description: orUndefined(payload.description),
      method: payload.type === "recette" ? payload.method : undefined,
    }),
  );
}

export async function updateTransaction(id: string, patch: Partial<Transaction> & Pick<Transaction, "type" | "cycleId">): Promise<Transaction> {
  const categoryId =
    patch.categoryId !== undefined || patch.chargeId !== undefined
      ? await categoryFor({ type: patch.type, categoryId: patch.categoryId ?? "", chargeId: patch.chargeId, cycleId: patch.cycleId })
      : undefined;
  return toTransaction(
    await http.put<ApiTransaction>(`/transactions/${id}`, {
      categoryId,
      amount: patch.amount,
      date: patch.date,
      cycleId: Number(patch.cycleId),
      description: patch.description === undefined ? undefined : orUndefined(patch.description),
      method: patch.type === "recette" ? patch.method : undefined,
    }),
  );
}

export async function deleteTransaction(id: string): Promise<void> {
  await http.delete(`/transactions/${id}`);
}

/* -------------------------------------------------------------------------- */
/* Calculs (peuvent rester côté client ou être délégués à l'API plus tard)      */
/* -------------------------------------------------------------------------- */

export interface CycleBalance {
  cycleId: string;
  recettes: number;
  depenses: number;
  solde: number;
}

export function computeTotals(rows: Transaction[]): {
  recettes: number;
  depenses: number;
  solde: number;
} {
  const recettes = rows.filter((t) => t.type === "recette").reduce((s, t) => s + t.amount, 0);
  const depenses = rows.filter((t) => t.type === "depense").reduce((s, t) => s + t.amount, 0);
  return { recettes, depenses, solde: recettes - depenses };
}

export function computeByCycle(rows: Transaction[], cycleIds: string[]): CycleBalance[] {
  return cycleIds.map((cycleId) => {
    const scoped = rows.filter((t) => t.cycleId === cycleId);
    const { recettes, depenses, solde } = computeTotals(scoped);
    return { cycleId, recettes, depenses, solde };
  });
}

export function topExpenses(rows: Transaction[], limit = 5): Transaction[] {
  return rows
    .filter((t) => t.type === "depense")
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);
}

export function byType(type: TransactionType): (t: Transaction) => boolean {
  return (t) => t.type === type;
}

/* -------------------------------------------------------------------------- */
/* Résolution des libellés d'opérations                                         */
/* -------------------------------------------------------------------------- */

export interface FinanceRefs {
  categories: FinanceCategory[];
  chargeCategories: ChargeCategory[];
  charges: Charge[];
}

export interface TxRef {
  label: string;
  icon: string;
  familyName?: string;
}

/**
 * Détermine le libellé (et l'icône) d'une opération :
 * - recette → nom de la catégorie de recette ;
 * - dépense → nom de la charge concernée (+ sa catégorie de charges).
 */
export function resolveTxRef(t: Transaction, refs: FinanceRefs): TxRef {
  if (t.type === "recette") {
    const category = refs.categories.find((c) => c.id === t.categoryId);
    return { label: category?.name ?? "—", icon: category?.icon ?? "ri-price-tag-3-line" };
  }
  const charge = refs.charges.find((c) => c.id === t.chargeId);
  const family = charge ? refs.chargeCategories.find((f) => f.id === charge.categoryId) : undefined;
  return {
    label: charge?.name ?? "—",
    icon: charge?.icon ?? "ri-price-tag-3-line",
    familyName: family?.name,
  };
}

/* -------------------------------------------------------------------------- */
/* Tableau prévisionnel des décaissements (prévu vs réalisé)                    */
/* -------------------------------------------------------------------------- */

export interface DisbursementLine {
  label: string;
  icon: string;
  /** Montant prévisionnel mensuel (somme des cycles du périmètre). */
  monthly: number;
  /** Montant prévisionnel annuel (mensuel × 12). */
  annual: number;
  /** Montant réellement décaissé sur le périmètre. */
  actual: number;
  /** Écart prévisionnel annuel − réalisé. */
  gap: number;
}

export interface DisbursementGroup {
  id: string;
  title: string;
  lines: DisbursementLine[];
  totalMonthly: number;
  totalAnnual: number;
  totalActual: number;
  totalGap: number;
}

export interface DisbursementReport {
  groups: DisbursementGroup[];
  grandMonthly: number;
  grandAnnual: number;
  grandActual: number;
  grandGap: number;
}

/**
 * Croise le budget saisi (charges prévisionnelles) et les dépenses réellement
 * enregistrées, regroupés par catégorie de charges.
 * Scope : "all" (global) ou l'identifiant d'un cycle.
 */
export function buildDisbursementReport(
  transactions: Transaction[],
  charges: Charge[],
  chargeCategories: ChargeCategory[],
  scope: string,
): DisbursementReport {
  const inScope = (cycleId: string) => scope === "all" || cycleId === scope;

  const groups: DisbursementGroup[] = chargeCategories.map((category) => ({
    id: category.id,
    title: category.name,
    lines: [],
    totalMonthly: 0,
    totalAnnual: 0,
    totalActual: 0,
    totalGap: 0,
  }));

  charges.forEach((charge) => {
    const monthly = charge.amounts
      .filter((a) => inScope(a.cycleId))
      .reduce((sum, a) => sum + a.monthly, 0);
    const actual = transactions
      .filter((t) => t.type === "depense" && t.chargeId === charge.id && inScope(t.cycleId))
      .reduce((sum, t) => sum + t.amount, 0);

    if (monthly <= 0 && actual <= 0) return;

    const annual = monthly * 12;
    const group = groups.find((g) => g.id === charge.categoryId) ?? groups[groups.length - 1];
    if (!group) return;

    group.lines.push({
      label: charge.name,
      icon: charge.icon,
      monthly,
      annual,
      actual,
      gap: annual - actual,
    });
  });

  groups.forEach((group) => {
    group.lines.sort((a, b) => b.monthly - a.monthly);
    group.totalMonthly = group.lines.reduce((s, l) => s + l.monthly, 0);
    group.totalAnnual = group.lines.reduce((s, l) => s + l.annual, 0);
    group.totalActual = group.lines.reduce((s, l) => s + l.actual, 0);
    group.totalGap = group.lines.reduce((s, l) => s + l.gap, 0);
  });

  const visibleGroups = groups.filter((g) => g.lines.length > 0);

  return {
    groups: visibleGroups,
    grandMonthly: visibleGroups.reduce((s, g) => s + g.totalMonthly, 0),
    grandAnnual: visibleGroups.reduce((s, g) => s + g.totalAnnual, 0),
    grandActual: visibleGroups.reduce((s, g) => s + g.totalActual, 0),
    grandGap: visibleGroups.reduce((s, g) => s + g.totalGap, 0),
  };
}